import { useLayoutEffect, useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { Ico } from "../../../components/icons/icons.jsx";
import { AssetTypeColumnEditor } from "../../Accounts/components/AssetTypeColumnEditor.jsx";
import {
  DEVICE_TYPE_STATUSES,
  deviceTypeFormToPayload,
  deviceTypeToForm,
  validateDeviceTypeForm,
} from "../deviceTypeUtils.js";

const fieldLabel = (c) => ({
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: c.sub,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
});

const inputStyle = (c, hasError) => ({
  width: "100%",
  height: 38,
  padding: "0 12px",
  borderRadius: 8,
  border: `1px solid ${hasError ? "#EF4444" : c.cardBdr}`,
  background: c.card,
  color: c.text,
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
});

export function DeviceTypeModal({ open, onClose, onSave, editing = null, saving = false }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [form, setForm] = useState(deviceTypeToForm(null));
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(editing);

  useLayoutEffect(() => {
    if (!open) return;
    setForm(deviceTypeToForm(editing));
    setErrors({});
    setSubmitError(null);
  }, [open, editing]);

  if (!open) return null;

  const handleSubmit = async () => {
    const nextErrors = validateDeviceTypeForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitError(null);
    try {
      await onSave(deviceTypeFormToPayload(form));
    } catch (err) {
      setSubmitError(err.message ?? "Failed to save device type");
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-type-modal-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(560px, 96vw)",
          maxHeight: "min(90vh, 760px)",
          zIndex: 71,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 14,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ModalShellHeader
          title={isEdit ? "Edit device type" : "Create device type"}
          titleId="device-type-modal-title"
          subtitle={isEdit ? editing.name : "Define a hardware model and its telemetry schema"}
          icon={Ico.model}
          onClose={onClose}
          closeLabel="Close device type form"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
        />
        <div style={{ padding: "16px 20px 20px", overflow: "auto" }}>
          <label style={{ display: "block", marginBottom: 14 }}>
            <span style={fieldLabel(c)}>Device type name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. GPS Tracker"
              style={inputStyle(c, errors.name)}
            />
            {errors.name && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.name}</span>}
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <label style={{ display: "block" }}>
              <span style={fieldLabel(c)}>Manufacturer</span>
              <input
                type="text"
                value={form.manufacturer}
                onChange={(e) => setForm((prev) => ({ ...prev, manufacturer: e.target.value }))}
                placeholder="e.g. Intellicar"
                style={inputStyle(c, errors.manufacturer)}
              />
              {errors.manufacturer && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.manufacturer}</span>}
            </label>
            <label style={{ display: "block" }}>
              <span style={fieldLabel(c)}>Protocol</span>
              <input
                type="text"
                value={form.protocol}
                onChange={(e) => setForm((prev) => ({ ...prev, protocol: e.target.value }))}
                placeholder="e.g. TCP, MQTT"
                style={inputStyle(c, errors.protocol)}
              />
              {errors.protocol && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.protocol}</span>}
            </label>
          </div>

          <label style={{ display: "block", marginBottom: 18 }}>
            <span style={fieldLabel(c)}>Status</span>
            <select
              value={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              style={{ ...inputStyle(c, errors.status), cursor: "pointer" }}
            >
              {DEVICE_TYPE_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            {errors.status && <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444" }}>{errors.status}</span>}
          </label>

          <AssetTypeColumnEditor
            columns={form.columns}
            errors={errors.columns ?? []}
            onChange={(columns) => setForm((prev) => ({ ...prev, columns }))}
            entityLabel="device type"
          />

          {submitError && (
            <p style={{ margin: "14px 0 0", fontSize: 12, color: "#EF4444", fontWeight: 600 }}>{submitError}</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 600, cursor: "pointer" }}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create device type"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
